import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test';

function ServiceDetails() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [service, setService] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    fetchServiceDetails();
  }, [serviceId]);

  const fetchServiceDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/services/${serviceId}`);
      setService(response.data);

      // Fetch provider details
      const providerResponse = await axios.get(`${API}/users/${response.data.provider_id}/profile`);
      setProvider(providerResponse.data.user);
    } catch (error) {
      console.error('Error fetching service:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = () => {
    if (!isAuthenticated) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + `/service/${serviceId}`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setShowPayment(true);
  };

  const createOrder = async () => {
    try {
      const response = await axios.post(
        `${API}/payments/create-order?service_id=${serviceId}`,
        {},
        { withCredentials: true }
      );
      setOrderId(response.data.order_id);
      return response.data.paypal_order_id;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      await axios.post(
        `${API}/payments/capture-order/${data.orderID}`,
        {},
        { withCredentials: true }
      );
      alert('Payment successful! Check your dashboard for order details.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error capturing payment:', error);
      alert('Payment failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Service not found.</p>
      </div>
    );
  }

  const isOwnService = user && service.provider_id === user.user_id;
  const platformFee = service.price * 0.15;
  const providerAmount = service.price - platformFee;

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="service-details-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/browse')}
          className="mb-6 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          data-testid="back-to-browse-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Browse
        </button>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {service.image_url ? (
            <img
              src={service.image_url}
              alt={service.title}
              className="w-full h-96 object-cover"
              data-testid="service-image"
            />
          ) : (
            <div className="w-full h-96 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
              <svg className="w-24 h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="service-title">
                  {service.title}
                </h1>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 font-medium rounded-lg">
                  {service.category}
                </span>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-blue-600" data-testid="service-price">
                  ${service.price}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Provider gets: ${providerAmount.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 text-lg whitespace-pre-wrap" data-testid="service-description">
                {service.description}
              </p>
            </div>

            {provider && (
              <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About the Provider</h2>
                <div className="flex items-center gap-4">
                  {provider.picture && (
                    <img
                      src={provider.picture}
                      alt={provider.name}
                      className="w-16 h-16 rounded-full"
                      data-testid="provider-avatar"
                    />
                  )}
                  <div>
                    <p className="text-lg font-semibold text-gray-900" data-testid="provider-name">
                      {provider.name}
                    </p>
                    <p className="text-gray-600">{provider.email}</p>
                    <button
                      onClick={() => navigate(`/provider/${provider.user_id}`)}
                      className="text-blue-600 hover:text-blue-700 mt-1"
                      data-testid="view-provider-profile-btn"
                    >
                      View Profile →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!showPayment ? (
              <div>
                {isOwnService ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-testid="own-service-notice">
                    <p className="text-yellow-800">This is your service. You cannot purchase it.</p>
                  </div>
                ) : (
                  <button
                    onClick={handlePurchase}
                    className="w-full py-4 bg-blue-600 text-white rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors"
                    data-testid="purchase-service-btn"
                  >
                    Purchase This Service
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 p-6 rounded-lg" data-testid="payment-section">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Complete Payment</h3>
                <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID }}>
                  <PayPalButtons
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      alert('Payment error. Please try again.');
                    }}
                  />
                </PayPalScriptProvider>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServiceDetails;